import { css } from '@emotion/core';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Theme,
    Typography,
    useTheme
} from '@material-ui/core';
import React from 'react';
import { ModelParameters } from 'shared/common/llm';
import { CandidateScoreEval } from '../graphql/queries/scoring-evals';
import { useSnackbar } from '../hooks/use-snackbar';
import { ProfileScoringEvalForm, ProfileScoringEvalFormData } from './profile-scoring-eval-form';

const styles = (theme: Theme) => css`
    .MuiPaper-root {
        min-width: 720px;
    }

    .MuiDialogContent-root {
        border-top: 1px solid ${theme.palette.divider};
        border-bottom: 1px solid ${theme.palette.divider};
        padding-top: 16px;
        padding-bottom: 16px;
    }

    .action-items {
        padding: 10px;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .MuiIconButton-root {
            margin-right: 10px;
        }
    }

    .MuiInputBase-root.Mui-disabled {
        color: ${theme.palette.text.primary};
    }
`;

const defaultModelParameters: ModelParameters = {
    frequency_penalty: 0,
    max_tokens: 6144,
    model: 'gpt-4o-2024-08-06',
    presence_penalty: 0,
    temperature: 0,
    top_p: 1
};

const modelOptions = ['gpt-4o-2024-08-06', 'claude-3-5-sonnet-20241022'];

export const CandidateScoreEvalForm: React.FC<{
    data?: CandidateScoreEval;
    open: boolean;
    onClose: () => void;
    onSave?: (data: Partial<CandidateScoreEval>) => Promise<void>;
}> = ({ data: initialData, open, onClose, onSave }) => {
    const theme = useTheme();
    const [data, setData] = React.useState<CandidateScoreEval | null>(initialData);
    const [saving, setSaving] = React.useState(false);
    const { setSnackbar } = useSnackbar();
    const disabled = !!data?.id || saving;

    const defaults: ProfileScoringEvalFormData = {
        modelParameters: defaultModelParameters,
        systemPrompt: '',
        title: '',
        userPromptTemplate: ''
    };

    const validateForm = () => {
        return data?.title?.trim()?.length > 0;
    };

    const handleGenerateScores = async () => {
        if (validateForm()) {
            setSaving(true);
            setSnackbar('Queuing profile scores generation');
            try {
                await onSave(data);
                setSnackbar('Profile scores generation queued');
            } catch (e) {
                setSnackbar('Error generating profile scores');
            } finally {
                setSaving(false);
            }
        }
    };

    const handleChange = (newData: ProfileScoringEvalFormData) => {
        setData({ ...data, ...newData });
    };

    const generateButton =
        disabled || !onSave ? null : (
            <Button onClick={handleGenerateScores} disabled={!validateForm()}>
                Generate Scores
            </Button>
        );

    const formData = data?.id ? data : { ...defaults, ...data };

    return (
        <Dialog open={open} maxWidth="lg" css={styles(theme)}>
            <DialogTitle>
                <Typography variant="h4" component="div">
                    {data?.id ? 'View Profile Scoring' : 'New Profile Scoring'}
                    {data?.id ? ` - ${data.title}` : ''}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <ProfileScoringEvalForm
                    data={formData}
                    modelOptions={modelOptions}
                    isEditable={!data?.id}
                    onChange={handleChange}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    {data?.id ? 'Close' : 'Cancel'}
                </Button>
                {generateButton}
            </DialogActions>
        </Dialog>
    );
};
